// The Yearly Summaries tab (#515): STM002's "Yearly Maintenance Breakdown" — one column per contract
// year, sell and cost by cost category, PAYG (each area's year by year is the Scope PA tab). And the
// Summary's contract price box.
import { CONTRACT_CATEGORY_LABELS, type ContractCategory, type PaymentType } from '@higgins/m2q-pricing';
import type { MaintenancePricing } from '../lib/maintenance-pricing';
import { formatMoney, formatPercent, formatSell, formatTwo } from './surface-format';
import { Block, RateRow } from './rates-panel';

const CATEGORY_LABEL = CONTRACT_CATEGORY_LABELS;
const CELL = 'px-2 py-1 text-right tabular-nums whitespace-nowrap';
/** What each payment type does, in the template's terms (maintenance-payments.ts). */
const PAYMENT_NOTE: Record<PaymentType, string> = {
  PAYG: 'PAYG, as the template: each year’s sell is its cost grossed up at the contract’s one GP %, so year 1 is a little more than the repaints on their own.',
  'Even Spread': 'Even Spread, as the template: every year pays the same share of each cost category, plus the finance interest on the work done ahead of payment.',
  'Higher Year One': 'Higher Year One, as the template: year 1 pays the most (by the repaint’s cost), the later years the rest, plus the finance interest spread over them.',
};
const YEAR_EDGE = 'border-l border-slate-200';

function Money({ value, shown }: { value: number; shown: boolean }) {
  return <>{formatSell(value, shown)}</>;
}

export function YearlySummaryTab({ pricing, priceShown }: { pricing: MaintenancePricing; priceShown: boolean }) {
  const { yearly } = pricing;
  const pairs = (sell: number, cost: number, key: string) => [
    <td key={`${key}s`} className={`${CELL} ${YEAR_EDGE}`}>
      <Money value={sell} shown={priceShown} />
    </td>,
    <td key={`${key}c`} className={CELL}>
      {formatMoney(cost)}
    </td>,
  ];
  return (
    <div className="space-y-2">
      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-xs text-slate-500">
              <th className="px-2 py-1.5 text-left font-normal">Items/Descriptions</th>
              <th colSpan={2} className={`px-2 font-semibold text-slate-800 ${YEAR_EDGE}`}>
                Totals
              </th>
              {yearly.years.map((y) => (
                <th key={y.number} colSpan={2} className={`px-2 font-semibold text-slate-800 ${YEAR_EDGE}`}>
                  {y.label} <span className="font-normal text-slate-500">· {y.number}</span>
                </th>
              ))}
            </tr>
            <tr className="text-xs text-slate-500">
              <th />
              {[{ number: 0 }, ...yearly.years].map((y) => [
                <th key={`${y.number}s`} className={`px-2 text-right font-normal ${YEAR_EDGE}`}>
                  Sell $
                </th>,
                <th key={`${y.number}c`} className="px-2 text-right font-normal">
                  Cost $
                </th>,
              ])}
            </tr>
          </thead>
          <tbody>
            {(Object.keys(CATEGORY_LABEL) as ContractCategory[]).map((c) => (
              <tr key={c} className="border-t border-slate-100">
                <td className="px-2 py-1">{CATEGORY_LABEL[c]}</td>
                {pairs(yearly.totals[c].sell, yearly.totals[c].cost, 'total')}
                {yearly.years.map((y) => pairs(y.categories[c].sell, y.categories[c].cost, String(y.number)))}
              </tr>
            ))}
            <tr className="border-t border-slate-100 text-slate-500">
              <td className="px-2 py-1">Interest {yearly.paymentType === 'PAYG' && <span className="text-xs">(PAYG: none)</span>}</td>
              {[{ number: 0, interest: yearly.interest }, ...yearly.years].map((y) => pairs(y.interest, 0, String(y.number)))}
            </tr>
            <tr className="border-t-2 border-slate-300 bg-slate-50 font-semibold">
              <td className="px-2 py-1">Totals</td>
              {pairs(yearly.sell, yearly.cost, 'total')}
              {yearly.years.map((y) => pairs(y.sell, y.cost, String(y.number)))}
            </tr>
            <tr>
              <td className="px-2 py-1">GP%</td>
              {[{ number: 0 }, ...yearly.years].map((y) => (
                <td key={y.number} colSpan={2} className={`${CELL} ${YEAR_EDGE}`}>
                  {formatPercent(yearly.gp)}
                </td>
              ))}
            </tr>
            <tr className="text-slate-600">
              <td className="px-2 py-1">Hours · Litres</td>
              {[{ number: 0, hours: yearly.hours, litres: yearly.litres }, ...yearly.years].map((y) => (
                <td key={y.number} colSpan={2} className={`${CELL} ${YEAR_EDGE}`}>
                  {formatTwo(y.hours)} h · {formatTwo(y.litres)} L
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
      <p className="max-w-3xl text-xs text-slate-500">
        {PAYMENT_NOTE[yearly.paymentType]} Each year&rsquo;s cost is what every area does that year. Each area&rsquo;s work, year by year, is on the Scope PA tab.
      </p>
    </div>
  );
}

/** The Summary's price box for a maintenance option: the contract total under the repaint total. */
export function ContractTotals({ pricing, repaintSell, priceShown }: { pricing: MaintenancePricing; repaintSell: number; priceShown: boolean }) {
  const { contract, years, yearly } = pricing;
  return (
    <Block title={`Maintenance contract (${years} years, ${yearly.paymentType})`}>
      <table className="w-full">
        <tbody>
          <RateRow label="Repaint (as priced)" sell={formatSell(repaintSell, priceShown)} />
          <RateRow label="Servicing and later work" sell={formatSell(contract.sell - repaintSell, priceShown)} />
          {yearly.interest !== 0 && <RateRow label="Finance interest" sell={formatSell(yearly.interest, priceShown)} />}
          <RateRow label={<strong>Contract Sell</strong>} sell={formatSell(yearly.sell, priceShown)} />
          <RateRow label="Contract Cost · GP%" sell={formatMoney(yearly.cost)} cost={formatPercent(yearly.gp)} />
        </tbody>
      </table>
      <p className="mt-2 text-xs text-slate-500">Accepting writes one line per year per cost code (A01L, B01L …) and sets the opportunity&rsquo;s Maintenance Years, Schedule, Type and Tier. PAYG, Even Spread and Higher Year One can all be accepted: a financed contract&rsquo;s lines sell each year&rsquo;s payment, interest included, at the same cost.</p>
    </Block>
  );
}
